import { AsyncSeriesBailHook, AsyncSeriesHook } from './tapable/index';
import EventEmitter from 'eventemitter3';

import { LifecycleFns, LifecycleFn, AppTimeouts, LifecycleOptions } from './Def';
import { DEFAULE_TIMEOUTS } from './constant';
import type { AppError } from './AppError';

import { ProtectedEventEmitter } from './utils/ProtectedEventEmitter';
import { validateLifecycleFn } from './utils/validateLifecycleFn';
import { promiseIgnoreCatch } from './utils/promiseIgnoreCatch';
import { asyncParallel } from './utils/asyncParallel';
import { Debugger } from './utils/Debugger';
import { toArray } from './utils/toArray';

export interface LifecycleEvent {
    beforeload: void;
    afterload: void;
    loaderror: AppError;

    beforebootstrap: void;
    afterbootstrap: void;
    bootstraperror: AppError;

    beforemount: void;
    aftermount: void;
    mounterror: AppError;

    beforeunmount: void;
    afterunmount: void;
    unmounterror: AppError;

    beforeupdate: void;
    afterupdate: void;
    updateerror: AppError;
}

export type LifecycleHooks<CustomProps> = {
    beforeload: AsyncSeriesHook<void>;
    afterload: AsyncSeriesHook<void>;
    loaderror: AsyncSeriesHook<AppError>;

    beforebootstrap: AsyncSeriesHook<void>;
    afterbootstrap: AsyncSeriesHook<void>;
    bootstraperror: AsyncSeriesHook<AppError>;

    beforemount: AsyncSeriesHook<void>;
    aftermount: AsyncSeriesHook<void>;
    mounterror: AsyncSeriesHook<AppError>;

    beforeunmount: AsyncSeriesHook<void>;
    afterunmount: AsyncSeriesHook<void>;
    unmounterror: AsyncSeriesHook<AppError>;

    beforeupdate: AsyncSeriesHook<void>;
    afterupdate: AsyncSeriesHook<void>;
    updateerror: AsyncSeriesHook<AppError>;

    load: AsyncSeriesBailHook<void, LifecycleFns<CustomProps> | void>;
    bootstrap: AsyncSeriesBailHook<void, unknown>;
    mount: AsyncSeriesBailHook<void, unknown>;
    unmount: AsyncSeriesBailHook<void, unknown>;
    update: AsyncSeriesBailHook<{ params: CustomProps }, unknown>;
};

function createLifecycleHooks<T>(): LifecycleHooks<T> {
    return Object.freeze({
        beforeload: new AsyncSeriesHook<void>(),
        afterload: new AsyncSeriesHook<void>(),
        loaderror: new AsyncSeriesHook<AppError>(['error'], 'loaderror'),

        beforebootstrap: new AsyncSeriesHook<void>(),
        afterbootstrap: new AsyncSeriesHook<void>(),
        bootstraperror: new AsyncSeriesHook<AppError>(['error'], 'bootstraperror'),

        beforemount: new AsyncSeriesHook<void>(),
        aftermount: new AsyncSeriesHook<void>(),
        mounterror: new AsyncSeriesHook<AppError>(['error'], 'mounterror'),

        beforeunmount: new AsyncSeriesHook<void>(),
        afterunmount: new AsyncSeriesHook<void>(),
        unmounterror: new AsyncSeriesHook<AppError>(['error'], 'unmounterror'),

        beforeupdate: new AsyncSeriesHook<void>(),
        afterupdate: new AsyncSeriesHook<void>(),
        updateerror: new AsyncSeriesHook<AppError>(['error'], 'updateerror'),

        load: new AsyncSeriesBailHook<void, LifecycleFns<T> | void>(),
        bootstrap: new AsyncSeriesBailHook<void, unknown>(),
        mount: new AsyncSeriesBailHook<void, unknown>(),
        unmount: new AsyncSeriesBailHook<void, unknown>(),
        update: new AsyncSeriesBailHook<{ params: T }, unknown>(['params'], 'update'),
    });
}

export interface LifecycleShadow<CustomProps> {
    rootDOM: Element | null;
    hooks: LifecycleHooks<CustomProps>;
    customProps: CustomProps;
    readonly fns: {
        call: (
            fnName: keyof LifecycleFns<CustomProps>,
            arg: CustomProps,
            allowUndefined?: boolean,
            each?: (index: number, total: number) => void
        ) => ReturnType<LifecycleFn<CustomProps>>;
    };
    readonly on: <T extends keyof LifecycleEvent>(
        event: T,
        listener: (event: LifecycleEvent[T]) => unknown,
        context?: unknown
    ) => this;
    readonly once: <T extends keyof LifecycleEvent>(
        event: T,
        listener: (event: LifecycleEvent[T]) => unknown,
        context?: unknown
    ) => this;
    readonly off: <T extends keyof LifecycleEvent>(
        event: T,
        listener: (event: LifecycleEvent[T]) => unknown,
        context?: unknown
    ) => this;
}

export class Lifecycle<CustomProps> extends Debugger implements ProtectedEventEmitter<LifecycleEvent> {
    readonly #eventBus = new EventEmitter<keyof LifecycleEvent>();
    #lifecycleFns: LifecycleFns<CustomProps> | null = null;
    #options: LifecycleOptions<CustomProps>;
    #loadingPromise: Promise<void> | null = null;

    #exportedCache: LifecycleShadow<CustomProps> | null = null;

    readonly #rootDOM: string | Element;

    public declare hooks: LifecycleHooks<CustomProps>;

    constructor(def: LifecycleOptions<CustomProps>, rootDOM: string | Element) {
        super();
        this.#options = def;
        this.#rootDOM = rootDOM;

        Object.defineProperty(this, 'hooks', {
            value: createLifecycleHooks<CustomProps>(),
            enumerable: false,
            writable: false,
            configurable: false,
        });

        for (const key of Object.keys(this.hooks) as Array<keyof LifecycleEvent>) {
            if (/(^before|^after|error$)/.test(key))
                this.hooks[key].tap(`${this}`, arg => {
                    this.#emit<typeof key>(key, arg as LifecycleEvent[typeof key]);
                });
        }
    }

    protected get debugName(): string {
        return `lifecycle:${this.name}`;
    }

    public get customProps(): CustomProps {
        const cp = this.#options.customProps ?? ({} as CustomProps);

        if ('function' === typeof cp) {
            return (cp as (name: string, loc: Location) => CustomProps).call(null, this.name, window.location);
        }

        return cp;
    }

    public get options(): LifecycleOptions<CustomProps> {
        return this.#options;
    }

    public get timeouts(): AppTimeouts {
        return Object.assign({}, DEFAULE_TIMEOUTS, this.#options.timeouts);
    }

    public get name(): string {
        return this.#options.name;
    }

    public get [Symbol.toStringTag](): string {
        return 'Lifecycle';
    }

    public override toString(): string {
        return `${this[Symbol.toStringTag]}(${this.name})`;
    }

    public get rootDOM(): Element | null {
        return 'string' === typeof this.#rootDOM ? document.querySelector(this.#rootDOM) : this.#rootDOM;
    }

    #emit<T extends keyof LifecycleEvent>(event: T, payload: LifecycleEvent[T]): this {
        this.debug('Emit %s with %O.', event, payload);
        // Should not break down.
        try {
            this.#eventBus.emit(event, payload);
        } catch {
            //
        }
        return this;
    }

    public on<T extends keyof LifecycleEvent>(
        event: T,
        listener: (event: LifecycleEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.on(event, listener, context);
        return this;
    }

    public once<T extends keyof LifecycleEvent>(
        event: T,
        listener: (event: LifecycleEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.once(event, listener, context);
        return this;
    }

    public off<T extends keyof LifecycleEvent>(
        event: T,
        listener: (event: LifecycleEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.off(event, listener, context);
        return this;
    }

    public get export(): LifecycleShadow<CustomProps> {
        const getCustomProps = (): CustomProps => this.customProps;
        const getRootDOM = (): Element | null => this.rootDOM;

        if (!this.#exportedCache) {
            this.#exportedCache = Object.freeze({
                get rootDOM() {
                    return getRootDOM();
                },
                hooks: this.hooks,
                get customProps() {
                    return getCustomProps();
                },
                fns: Object.freeze({
                    call: (
                        fnName: keyof LifecycleFns<CustomProps>,
                        arg: CustomProps,
                        allowUndefined?: boolean,
                        each?: (index: number, total: number) => void
                    ): Promise<void> => {
                        return this.#callLifecycleFn(fnName, arg, allowUndefined, each);
                    },
                }),
                on: (...args): LifecycleShadow<CustomProps> => {
                    this.on(...args);
                    return this.export;
                },
                once: (...args): LifecycleShadow<CustomProps> => {
                    this.once(...args);
                    return this.export;
                },
                off: (...args): LifecycleShadow<CustomProps> => {
                    this.on(...args);
                    return this.export;
                },
            });
        }

        return this.#exportedCache;
    }

    /**
     * Load lifecycle function from lifecycle, assetsMap or entry.
     *
     * This function is concurrency-safe, for independent preload.
     * @returns void
     */
    public load(): Promise<void> {
        this.debug('Call load().');

        if (this.#lifecycleFns) {
            this.debug('The lifecycleFns already loaded.');
            return Promise.resolve();
        }

        if (this.#loadingPromise) {
            this.debug('During loading lifecycleFns.');
            return this.#loadingPromise;
        }

        this.#loadingPromise = promiseIgnoreCatch(this.hooks.beforeload.promise())
            .then(() => this.hooks.load.promise())
            .then(lifecycle => {
                if (!lifecycle) {
                    return Promise.reject(Error('No lifecycle loaded.'));
                }
                validateLifecycleFn(lifecycle);
                this.#lifecycleFns = lifecycle;
            })
            .then(
                () => promiseIgnoreCatch(this.hooks.afterload.promise()),
                err => promiseIgnoreCatch(this.hooks.loaderror.promise(err)).then(() => Promise.reject(err))
            )
            .finally(() => {
                this.#loadingPromise = null;
            });

        return this.#loadingPromise;
    }

    public async mount(): Promise<void> {
        this.debug('Call mount().');
        await promiseIgnoreCatch(this.hooks.beforemount.promise());

        try {
            await this.hooks.mount.promise();
        } catch (error: unknown) {
            await promiseIgnoreCatch(this.hooks.mounterror.promise(error as AppError));
            throw error;
        }

        await promiseIgnoreCatch(this.hooks.aftermount.promise());
    }

    public async bootstrap(): Promise<void> {
        this.debug('Call bootstrap().');

        await promiseIgnoreCatch(this.hooks.beforebootstrap.promise());

        try {
            await this.hooks.bootstrap.promise();
        } catch (error: unknown) {
            await promiseIgnoreCatch(this.hooks.bootstraperror.promise(error as AppError));
            throw error;
        }

        await promiseIgnoreCatch(this.hooks.afterbootstrap.promise());
    }

    public async unmount(): Promise<void> {
        this.debug('Call unmount().');
        await promiseIgnoreCatch(this.hooks.beforeunmount.promise());

        try {
            await this.hooks.unmount.promise();
        } catch (error: unknown) {
            await promiseIgnoreCatch(this.hooks.unmounterror.promise(error as AppError));
            throw error;
        }

        await promiseIgnoreCatch(this.hooks.afterunmount.promise());
    }

    public async update(customProps: CustomProps): Promise<void> {
        this.debug('Call update(%O).', customProps);

        await promiseIgnoreCatch(this.hooks.beforeupdate.promise());

        try {
            await this.hooks.update.promise({ params: customProps });
        } catch (error: unknown) {
            await promiseIgnoreCatch(this.hooks.updateerror.promise(error as AppError));
            throw error;
        }

        await promiseIgnoreCatch(this.hooks.afterupdate.promise());
    }

    /**
     * Clear all states.
     * @returns Promise<void>
     */
    public clear(): Promise<unknown> | unknown {
        this.debug('Call clear().');
        this.#lifecycleFns = null;
        this.#eventBus.removeAllListeners();
        return;
    }

    #callLifecycleFn(
        name: keyof LifecycleFns<CustomProps>,
        arg: CustomProps,
        allowUndefined?: boolean,
        each?: (index: number, total: number) => void
    ): Promise<void> {
        if (!this.#lifecycleFns) {
            return Promise.reject(Error(`Lifecycle fns of ${this} has not been loaded yet.`));
        }

        const fns = toArray(this.#lifecycleFns[name]).filter((fn): fn is LifecycleFn<CustomProps> => !!fn);

        if (!allowUndefined && fns.length === 0) {
            throw Error(`${this} has no ${name} lifecycle function.`);
        }

        return asyncParallel(
            fns,
            [
                {
                    ...arg,
                    domElement: this.#rootDOM,
                    name: this.name,
                },
            ],
            null,
            (index: number) => {
                if (each) each(index, fns.length);
            }
        );
    }
}
